import React, { useState, useEffect } from 'react';
import { Modal, Table, Tag, Space, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { LockOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { formatDateTime } from '@utils/dateTime';
import type { OperatorInventoryHoldRow } from '@services/operator/inventoryHoldService';

export interface HoldServiceLike {
  getRecords: (params: Record<string, unknown>) => Promise<{
    success: boolean;
    data?: { list?: OperatorInventoryHoldRow[] } | OperatorInventoryHoldRow[] | null;
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}

interface InventoryHoldsModalProps {
  open: boolean;
  onClose: () => void;
  inventoryId?: string;
  // 標題顯示用：該 inventory 對應的 batch / SKU（由列傳入，避免再打一次 API）。
  productSku?: string | null;
  batch?: string | null;
  holdService: HoldServiceLike;
}

interface HoldDisplayRow {
  id: string;
  number: string;
  reason: string;
  held_by: string;
  created_at: string;
  locked_quantity: number;
}

// 同一張保留單對同一 inventory 可能拆成多個 hold item，需把命中此 inventory 的 item.quantity 加總，
// 才是「本庫存在這張保留單被鎖的總量」。
function sumLockedQuantity(hold: OperatorInventoryHoldRow, inventoryId: string): number {
  return hold.items
    .filter((item) => item.inventory.id === inventoryId)
    .reduce((sum, item) => sum + item.quantity, 0);
}

const InventoryHoldsModal: React.FC<InventoryHoldsModalProps> = ({
  open,
  onClose,
  inventoryId,
  productSku,
  batch,
  holdService,
}) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<HoldDisplayRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !inventoryId) {
      setRows([]);
      return;
    }

    let cancelled = false;
    const fetchHolds = async () => {
      setLoading(true);
      try {
        const response = await holdService.getRecords({ inventory_id: inventoryId, status: ['active'] });
        if (cancelled) return;
        if (response.success) {
          const data = response.data;
          const list: OperatorInventoryHoldRow[] = Array.isArray(data) ? data : (data?.list ?? []);
          setRows(
            list.map((hold) => ({
              id: hold.id,
              number: hold.number,
              reason: hold.reason,
              held_by: hold.held_by,
              created_at: hold.created_at,
              locked_quantity: sumLockedQuantity(hold, inventoryId),
            })),
          );
        } else {
          message.error(t('common.message.fetch_failed') as string);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to fetch inventory holds:', error);
          message.error(t('common.message.fetch_failed') as string);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchHolds();
    return () => {
      cancelled = true;
    };
  }, [open, inventoryId, holdService, t]);

  const columns: ColumnsType<HoldDisplayRow> = [
    {
      title: t('models.inventory_hold.number'),
      dataIndex: 'number',
      key: 'number',
      render: (number: string, record: HoldDisplayRow) => (
        <Link to={`/inventory_holds/${record.id}`}>{number}</Link>
      ),
    },
    {
      title: t('models.inventory_hold.reason'),
      dataIndex: 'reason',
      key: 'reason',
      render: (reason: string) => (
        <Tag color="warning">
          {t(`enums.inventory_hold.reason.${reason}`, { defaultValue: reason })}
        </Tag>
      ),
    },
    {
      title: t('shared.inventory_holds.locked_quantity'),
      dataIndex: 'locked_quantity',
      key: 'locked_quantity',
      align: 'right',
      render: (qty: number) => <Tag color="warning">{qty}</Tag>,
    },
    {
      title: t('models.inventory_hold.held_by'),
      dataIndex: 'held_by',
      key: 'held_by',
      render: (heldBy: string) => heldBy || '-',
    },
    {
      title: t('common.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (createdAt: string) => formatDateTime(createdAt),
    },
  ];

  const titleSuffix = [batch, productSku].filter(Boolean).join(' · ');

  return (
    <Modal
      title={
        <Space>
          <LockOutlined style={{ color: '#faad14' }} />
          {t('shared.inventory_holds.holds_modal_title')}
          {titleSuffix && <span style={{ fontSize: 12, color: '#666' }}>{titleSuffix}</span>}
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={760}
      destroyOnClose
    >
      <Table
        columns={columns}
        dataSource={rows}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="small"
        locale={{ emptyText: t('shared.inventory_holds.no_active_holds') }}
      />
    </Modal>
  );
};

export default InventoryHoldsModal;
